import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row, FormGroup, Spinner, Alert } from "react-bootstrap";
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { createAuthService } from '../services/authService';
import { useAuthState } from '../hooks/useAuthState';
import bg1 from "../assets/img/bg1.jpg";
import logo from "../assets/img/brandimg.png";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .email('Email is invalid'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
});

const initialValues = {
  email: '',
  password: ''
};

export default function Signin() {
  const authState = useAuthState();
  const authService = createAuthService(authState);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      setSubmitting(true);
      const user = await authService.handleLogin(values);
      if (user) {
        // Use replace to prevent history stacking
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error.message);
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return;
    
    try {
      setIsGoogleLoading(true);
      setError('');
      const user = await authService.handleGoogleSignIn();
      if (user) {
        // Use replace to prevent history stacking
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      setError(error.message);
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="page-sign d-block py-0">
      <Row className="g-0">
        <Col md="7" lg="5" xl="4" className="col-wrapper">
          <Card className="card-sign">
            <Card.Header>
              <img src={logo} alt="logo" className="header-logo" style={{ width: '170px', height: '170px' }}/>
              <Card.Title>Sign In</Card.Title>
              <Card.Text>Welcome back! Please signin to continue.</Card.Text>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnMount={true}
              >
                {({ isSubmitting, errors, touched, dirty, isValid, handleSubmit }) => (
                  <Form onSubmit={handleSubmit} className="needs-validation" noValidate>
                    <FormGroup className="mb-3">
                      <Form.Label htmlFor="email">Email</Form.Label>
                      <Field
                        id="email"
                        name="email"
                        type="email"
                        className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                        placeholder="Enter your email"
                        disabled={isSubmitting || isGoogleLoading}
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-4">
                      <Form.Label htmlFor="password">Password</Form.Label>
                      <Field
                        id="password"
                        name="password"
                        type="password"
                        className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                        placeholder="Enter your password"
                        disabled={isSubmitting || isGoogleLoading}
                      />
                      {touched.password && errors.password && (
                        <div className="invalid-feedback d-block">
                          {errors.password}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup className="d-flex justify-content-between align-items-center mb-4">
                      <Link to="/auth/forgot" className="text-secondary">
                        <small>Forgot Password?</small>
                      </Link>
                    </FormGroup>

                    <Button 
                      type="submit" 
                      variant="primary"
                      className="btn-sign w-100"
                      disabled={!dirty || !isValid || isSubmitting || isGoogleLoading}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Signing in...
                        </>
                      ) : 'Sign In'}
                    </Button>

                    <div className="divider"><span>or sign in with</span></div>
                    
                    <Button 
                      type="button"
                      variant="outline-secondary"
                      className="btn-google w-100"
                      onClick={handleGoogleSignIn}
                      disabled={isSubmitting || isGoogleLoading}
                    >
                      {isGoogleLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Signing in with Google...
                        </>
                      ) : (
                        <>
                          <i className="ri-google-fill me-2"></i>
                          Google
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
            </Card.Body>
            <Card.Footer>
              Don't have an account? <Link to="/auth/signup">Create an Account</Link>
            </Card.Footer>
          </Card>
        </Col>
        <Col className="d-none d-lg-block">
          <img src={bg1} className="auth-img" alt="" style={{ 
            width: '100%',
            height: '100vh',
            objectFit: 'cover'
          }}/>
        </Col>
      </Row>
    </div>
  );
}